// Shared Web Audio engine for real-time waveforms (mic + AI TTS playback).
import client from "@/lib/api";

let _ctx = null;
export function getCtx() {
  if (!_ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (AC) _ctx = new AC();
  }
  if (_ctx && _ctx.state === "suspended") _ctx.resume().catch(() => {});
  return _ctx;
}

export function analyserFromStream(stream) {
  const ctx = getCtx();
  if (!ctx) return null;
  try {
    const src = ctx.createMediaStreamSource(stream);
    const an = ctx.createAnalyser();
    an.fftSize = 128;
    an.smoothingTimeConstant = 0.8;
    src.connect(an);
    return an;
  } catch { return null; }
}

let _current = null;
// Unlock/resume the audio engine inside a user gesture so the AI's opening line
// can autoplay (mobile browsers block audio until the first user interaction).
export function unlockAudio() {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  try {
    const buf = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start(0);
  } catch {}
}

// --- HTMLAudioElement playback (media channel) ---
// On iOS Safari, Web Audio API respects the hardware mute/ring switch, so TTS is
// silent when the phone is muted. An <audio> element plays through the MEDIA
// channel and ignores the silent switch, so we use it for TTS playback.
const SILENT_WAV = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=";
let _audioEl = null;
let _elUnlocked = false;
export function getAudioEl() {
  if (!_audioEl && typeof Audio !== "undefined") {
    _audioEl = new Audio();
    _audioEl.preload = "auto";
    _audioEl.setAttribute("playsinline", "");
  }
  return _audioEl;
}
// Must run synchronously inside a user gesture (tap) to unlock scripted media
// playback on iOS so later (post-fetch) play() calls are allowed.
export function unlockAudioEl() {
  const el = getAudioEl();
  if (!el || _elUnlocked) return;
  try {
    el.src = SILENT_WAV;
    const p = el.play();
    if (p && p.then) p.then(() => { _elUnlocked = true; }).catch(() => {});
  } catch {}
}

// Plays TTS audio and returns { analyser, done, stop }. done resolves when playback ends.
export async function playTTS(text, speed = 1.0) {
  try {
    if (_current) { _current.stop(); _current = null; }
    const { data } = await client.post("/tts", { text, speed });
    const ctx = getCtx();
    const bytes = Uint8Array.from(atob(data.audio_base64), (c) => c.charCodeAt(0));
    if (!ctx) throw new Error("no audio ctx");
    const buffer = await ctx.decodeAudioData(bytes.buffer.slice(0));
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.82;
    source.connect(analyser);
    analyser.connect(ctx.destination);
    let resolveDone;
    const done = new Promise((res) => (resolveDone = res));
    source.onended = () => { resolveDone(); if (_current && _current.source === source) _current = null; };
    source.start(0);
    const handle = { analyser, done, source, stop: () => { try { source.stop(); } catch {} resolveDone(); } };
    _current = handle;
    return handle;
  } catch (e) {
    // fallback: browser speech synthesis, no analyser
    if (window.speechSynthesis) {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "de-DE"; u.rate = speed;
      const done = new Promise((res) => { u.onend = res; u.onerror = res; setTimeout(res, 6000); });
      window.speechSynthesis.speak(u);
      return { analyser: null, done, stop: () => window.speechSynthesis.cancel() };
    }
    return { analyser: null, done: Promise.resolve(), stop: () => {} };
  }
}

export const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Pick a MediaRecorder mime the browser actually supports. iOS Safari does NOT
// support audio/webm (it records audio/mp4); forcing webm yields a file Whisper
// cannot decode, so we must record + label the real type.
export function pickAudioMime() {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return "";
  const candidates = [
    "audio/webm;codecs=opus", "audio/webm",
    "audio/mp4;codecs=mp4a.40.2", "audio/mp4",
    "audio/ogg;codecs=opus", "audio/ogg",
  ];
  for (const m of candidates) if (MediaRecorder.isTypeSupported(m)) return m;
  return "";
}

export function extForMime(mime) {
  if (!mime) return "webm";
  if (mime.includes("mp4") || mime.includes("m4a")) return "mp4";
  if (mime.includes("ogg")) return "ogg";
  if (mime.includes("wav")) return "wav";
  return "webm";
}
